import React from 'react';
import ReactMarkdown from 'react-markdown';
import Link from '@amzn/awsui-components-core/link';
import { Badge } from '../Badge/Badge';
import { CtaButton } from '../CtaButton/CtaButton';
import type { CustomIconName } from '../CustomIcon/CustomIcon';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import './SectionHeader.scss';

/**
 * SectionHeader — internal helper used by ContentGrid and CardGrid.
 *
 * Renders the canonical block header for those two components. Not
 * exported from the package's public surface; if you find yourself
 * wanting to use it elsewhere, prefer composing one of the existing
 * blocks or extend the public API deliberately.
 *
 * Layout:
 *   [optional badge pill]
 *   [heading                          ] [optional CTA button]
 *   [optional subhead, max-width 960px]
 *
 * The CTA button sits on the same row as the heading (right-aligned) and
 * renders as a Secondary / Medium CtaButton. The subhead wraps below.
 *
 * Returns `null` only when every slot is empty — heading-only, badge-only,
 * subheading-only, and CTA-only call sites all render their slot.
 *
 * Spacing follows the design primitives:
 * - badge → heading: var(--poly-space-headerBadge-toHeading)
 * - heading → subheading: var(--poly-space-headerHeading-toSubheading)
 * - heading ↔ button gap on the row: var(--poly-space-headerHeading-toSubheading)
 *
 * Typography follows the SPS Foundations type ramp:
 * - heading: Heading 400 (52px desktop, mobile-heading-500 at tablet)
 * - subheading: Subhead 300 (24px desktop, Subhead 200 at tablet)
 */

export interface SectionHeaderProps {
  /** Optional badge pill text (e.g. "New Seller Incentives") */
  badge?: string;

  /** Optional CustomIcon name to render inside the badge pill (default: "star") */
  badgeIcon?: CustomIconName;

  /** Section heading (renders as h2) */
  heading?: string;

  /** ID applied to the h2 — useful for aria-labelledby on the parent <section> */
  headingId?: string;

  /** Section subheading paragraph (max-width 960px) */
  subheading?: string;

  /** Optional CTA button label rendered on the same row as the heading */
  ctaText?: string;

  /** Optional CTA button href */
  ctaHref?: string;

  /** Whether the CTA opens in the current tab or a new one */
  ctaBehavior?: CtaBehavior;

  /** What the CTA does. */
  ctaAction?: CtaAction;

  /** Popup ID to open when the CTA action is Popup. */
  ctaTargetPopupId?: string;

  /**
   * When true, keep the desktop header layout (heading + CTA side-by-side,
   * larger heading, 24px subhead) through the tablet range and only collapse
   * to the stacked / full-width-CTA mobile layout at the phone breakpoint
   * (≤mobile). Default false = collapse at ≤tablet. Carousel opts in per its
   * mocks; ContentGrid/CardGrid keep the default tablet flip.
   */
  stackAtPhone?: boolean;

  /**
   * Extra className appended to the root header element. Used by host
   * components to apply block-level positioning (margin/padding) without
   * changing the shared header internals.
   */
  className?: string;
}

/**
 * Whether SectionHeader renders anything for these slots. Host blocks use it
 * to style themselves differently when no header is shown.
 */
export function hasSectionHeaderContent({
  heading,
  badge,
  subheading,
  ctaText,
  ctaHref,
  ctaAction,
  ctaTargetPopupId,
}: Pick<SectionHeaderProps, 'heading' | 'badge' | 'subheading' | 'ctaText' | 'ctaHref' | 'ctaAction' | 'ctaTargetPopupId'>): boolean {
  return !!(heading || badge || subheading || (ctaText && hasCtaDestination(ctaHref, ctaAction, ctaTargetPopupId)));
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  badge,
  badgeIcon = 'star',
  heading,
  headingId,
  subheading,
  ctaText,
  ctaHref,
  ctaBehavior,
  ctaAction,
  ctaTargetPopupId,
  stackAtPhone,
  className,
}) => {
  const hasCta = !!(ctaText && hasCtaDestination(ctaHref, ctaAction, ctaTargetPopupId));
  if (!hasSectionHeaderContent({
    heading,
    badge,
    subheading,
    ctaText,
    ctaHref,
    ctaAction,
    ctaTargetPopupId,
  })) return null;

  const rootClass = `poly-section-header${stackAtPhone ? ' poly-section-header--stack-phone' : ''}${className ? ` ${className}` : ''}`;

  return (
    <header className={rootClass}>
      {badge && (
        <Badge size="md" tone="nsi" icon={badgeIcon} className="poly-section-header__badge">
          {badge}
        </Badge>
      )}
      {(heading || hasCta) && (
        <div className="poly-section-header__heading-row">
          {heading && (
            <h2 id={headingId} className="poly-section-header__heading">
              {heading}
            </h2>
          )}
          {hasCta && (
            <div className="poly-section-header__cta">
              <CtaButton
                href={ctaHref}
                behavior={ctaBehavior}
                action={ctaAction}
                popupTargetId={ctaTargetPopupId}
                colorVariant="secondary"
                size="medium"
              >
                {ctaText}
              </CtaButton>
            </div>
          )}
        </div>
      )}
      {subheading && (
        <p className="poly-section-header__subheading">
          <ReactMarkdown
            components={{
              p: ({ children }) => <>{children}</>,
              a: ({ href, children }) => (
                <Link href={href ?? ''} variant="secondary" fontSize="inherit">
                  {children}
                </Link>
              ),
            }}
          >
            {subheading}
          </ReactMarkdown>
        </p>
      )}
    </header>
  );
};
